import {
  DATASET_ACCESS_ERROR_CODES,
  MODEL_ACCESS_ERROR_CODES
} from "@/components/Intelligence/Training/constants";
import { toast } from "@/components/ui/use-toast";
import env from "@/env";
import {
  CreateTrainingJobResponse,
  GetTrainingJobsResponse,
  NormalizedTrainingJob,
  TrainingForm,
  TrainingJobFromResponse,
  TrainingProgress,
  TrainingUsageDetails
} from "@/types/training";
import { executeAPIRequest } from "@/utils/api";
import {
  getCreateTrainingRequestBody,
  getFieldErrorObject,
  normalizeBaseModel,
  normalizeJob
} from "@/utils/training";
import { datasetInfo, downloadFile, listFiles } from "@huggingface/hub";
import { AxiosError } from "axios";
import { create } from "zustand";

export const useTrainingStore = create<{
  isJobsLoading: boolean;
  isJobDetailsLoading: boolean;
  isModelDownloading: boolean;
  isTrainingLoading: boolean;
  isAbortLoading: boolean;
  isTrainingProgressLoading: boolean;
  trainingProgressData: TrainingProgress[] | null;
  jobs: number;
  jobDetails: NormalizedTrainingJob | null;
  trainingCreationError: string | null;
  errorObject: Record<string, string>;
  resetTrainingStore: () => void;
  fetchJobs: (options: { page: number; limit: number }) => Promise<{
    results: NormalizedTrainingJob[];
    resultCount: number;
  }>;
  fetchJobDetails: (jobId: string, hideLoader?: boolean) => Promise<void>;
  startTraining: (formValues: TrainingForm) => Promise<boolean>;
  abortTraining: (jobId: string) => Promise<void>;
  fetchLossChartData: (jobId: string) => Promise<Blob | null>;
  checkDatasetAccess: (datasetName: string, accessToken?: string) => Promise<void>;
  checkModelAccess: (modelName: string, accessToken?: string) => Promise<void>;
  fetchLogs: (
    jobId: string,
    tab: "stdout" | "stderr",
    abortController?: AbortController
  ) => Promise<ReadableStream>;
  getAvailableModels: () => Promise<{ value: string; label: string }[]>;
  getAvailableDatasets: () => Promise<{ value: string; label: string }[]>;
  getUsageDetails: () => Promise<void>;
  usageDetails: TrainingUsageDetails;
  clearError: () => void;
  fetchTrainingProgress: (jobId: string) => Promise<{
    training_progress: {
      logs: TrainingProgress[];
    };
  } | null>;
}>((set, get) => {
  const url = env.API_URL_TAAS;

  return {
    isJobsLoading: false,
    isJobDetailsLoading: false,
    isModelDownloading: false,
    isTrainingLoading: false,
    isAbortLoading: false,
    isTrainingProgressLoading: false,
    trainingProgressData: null,
    trainingCreationError: null,
    jobs: 0,
    jobDetails: null,
    errorObject: {},
    usageDetails: {
      daily_quota: 0,
      used: 0,
      remaining: 0,
      unlimited: false
    },
    resetTrainingStore: () => {
      set({
        isJobsLoading: false,
        isJobDetailsLoading: false,
        isModelDownloading: false,
        isTrainingLoading: false,
        isAbortLoading: false,
        isTrainingProgressLoading: false,
        trainingProgressData: null,
        trainingCreationError: null,
        jobs: 0,
        jobDetails: null
      });
    },
    fetchJobs: async (options) => {
      set({ isJobsLoading: true });
      if (!env.FEATUREFLAG_TRAINING_ENABLED) {
        set({ jobs: 0 });
        return {
          resultCount: 0,
          results: []
        };
      }
      const offset = (options.page - 1) * options.limit;
      const response = await executeAPIRequest<GetTrainingJobsResponse>({
        method: "get",
        url: `${url}/taas/jobs?offset=${offset}&sort_by=created_at&sort_order=desc&limit=${options.limit}`,
        options: {
          replaceUrl: true
        }
      });
      set({ isJobsLoading: false });
      set({ jobs: response.total });
      return {
        resultCount: response.total,
        results: response.jobs.map(normalizeJob)
      };
    },
    fetchJobDetails: async (jobId: string, hideLoader?: boolean) => {
      if (!hideLoader) {
        set({ isJobDetailsLoading: true });
      }
      try {
        const response = await executeAPIRequest<TrainingJobFromResponse>({
          method: "get",
          url: `${url}/taas/jobs/${jobId}`,
          options: {
            replaceUrl: true
          }
        });
        set({ jobDetails: response ? normalizeJob(response) : null });
      } catch (error) {
        console.error("Error fetching job details:", error);
        const errorMessage = (error as Error).message || "Failed to fetch jobs";
        toast({ title: errorMessage, variant: "error" });
      } finally {
        set({ isJobDetailsLoading: false });
      }
    },
    startTraining: async (formValues) => {
      set({ isTrainingLoading: true, trainingCreationError: null });
      try {
        if (!env.FEATUREFLAG_TRAINING_ENABLED) {
          return false;
        }
        const response = await executeAPIRequest<CreateTrainingJobResponse>({
          url: `${url}/taas/jobs`,
          method: "post",
          options: {
            replaceUrl: true,
            publicError: true,
            axiosRetries: 0,
            data: getCreateTrainingRequestBody(formValues)
          }
        });
        get().getUsageDetails();
        window.location.href = `/ai/training/job/${response.id}`;
        return true;
      } catch (e) {
        console.error("Error starting training:", e);
        if (e instanceof AxiosError) {
          if (e.status === 422) {
            if (Array.isArray(e.response?.data?.detail)) {
              const { errorMessage, errorObject } = getFieldErrorObject(e.response.data.detail);
              set({ trainingCreationError: errorMessage, errorObject });
            } else {
              set({
                trainingCreationError:
                  e.response?.data?.detail || "Validation error with training configuration"
              });
            }
          } else if (e.status === 429) {
            set({ trainingCreationError: e.response?.data?.detail || "Quota exceeded" });
          } else {
            const errorMessage = e.response?.data?.detail || e.response?.data?.error;
            set({ trainingCreationError: errorMessage || "Failed to start training" });
          }
        } else {
          const errorMessage = (e as Error).message || "Failed to start training";
          set({ trainingCreationError: errorMessage });
        }
        return false;
      } finally {
        set({ isTrainingLoading: false });
      }
    },
    abortTraining: async (jobId: string) => {
      set({ isAbortLoading: true });
      try {
        await executeAPIRequest({
          url: `${url}/taas/jobs/${jobId}/abort`,
          method: "post",
          options: {
            replaceUrl: true
          }
        });
      } catch (error) {
        console.error("Error aborting training:", error);
      } finally {
        set({ isAbortLoading: false });
      }
    },
    fetchLossChartData: async (jobId: string) => {
      try {
        const response = await executeAPIRequest<Blob>({
          method: "get",
          url: `${url}/taas/jobs/${jobId}/loss-chart`,
          options: { replaceUrl: true, responseType: "blob", axiosRetries: 0 }
        });
        return response;
      } catch (error) {
        console.error("Error fetching loss chart data:", error);
        return null;
      }
    },
    checkDatasetAccess: async (datasetName: string, accessToken?: string): Promise<void> => {
      try {
        const info = await datasetInfo({ name: datasetName, accessToken });
        const isGated = info.gated || info.private;

        const files = listFiles({
          repo: { type: "dataset", name: datasetName },
          recursive: false
        });

        let hasFiles = false;
        for await (const file of files) {
          console.log(`Found file: ${file.path}`);
          hasFiles = true;
          break;
        }

        if (!hasFiles) {
          throw new Error(DATASET_ACCESS_ERROR_CODES.empty);
        }

        console.log(`📊 ${datasetName} - ✅ ACCESSIBLE - ${isGated ? "🔒 GATED" : "🔓 PUBLIC"}`);
      } catch (err) {
        if (
          err instanceof Error &&
          (err.message.includes("401") || err.message.includes("gated"))
        ) {
          throw new Error(DATASET_ACCESS_ERROR_CODES.gated);
        } else {
          throw new Error(DATASET_ACCESS_ERROR_CODES.notFound);
        }
      }
    },
    checkModelAccess: async (modelName: string, accessToken?: string): Promise<void> => {
      try {
        // Try to list files
        const files = listFiles({ repo: { type: "model", name: modelName }, accessToken });
        let hasFiles = false;
        let testFile: string | null = null;

        for await (const file of files) {
          hasFiles = true;
          testFile = file.path;
          break; // Just get first file for testing
        }

        if (!hasFiles) {
          throw new Error(MODEL_ACCESS_ERROR_CODES.empty);
        }

        // Try to download a file to test access
        try {
          if (testFile) {
            await downloadFile({
              repo: { type: "model", name: modelName },
              path: testFile,
              accessToken
            });
          }
        } catch (downloadErr) {
          if (
            downloadErr instanceof Error &&
            (downloadErr.message.includes("gated") || downloadErr.message.includes("401"))
          ) {
            throw new Error(MODEL_ACCESS_ERROR_CODES.gated);
          } else {
            throw new Error(MODEL_ACCESS_ERROR_CODES.general);
          }
        }
      } catch (err) {
        if (
          err instanceof Error &&
          (err.message.includes("401") || err.message.includes("gated"))
        ) {
          throw new Error(MODEL_ACCESS_ERROR_CODES.unauthorized);
        } else {
          throw new Error(MODEL_ACCESS_ERROR_CODES.failedAccess);
        }
      }
    },
    fetchLogs: async (jobId: string, tab: "stdout" | "stderr", abortController) => {
      const response = await executeAPIRequest<ReadableStream>({
        method: "get",
        url: `${url}/taas/jobs/${jobId}/logs/stream?stream=${tab}`,
        options: {
          adapter: "fetch",
          responseType: "stream",
          publicError: true,
          timeout: 600000,
          abortController,
          replaceUrl: true
        }
      });
      return response;
    },
    getAvailableModels: async () => {
      const response = await executeAPIRequest<{
        models: { display_name: string; hf_link: string }[];
      }>({
        method: "get",
        url: `${url}/taas/base-models`,
        options: {
          replaceUrl: true
        }
      });
      return response.models.map(normalizeBaseModel);
    },
    getAvailableDatasets: async () => {
      const response = await executeAPIRequest<{
        datasets: { display_name: string; hf_link: string }[];
      }>({
        method: "get",
        url: `${url}/taas/datasets`,
        options: {
          replaceUrl: true
        }
      });
      return response.datasets.map((dataset) => ({
        value: dataset.display_name,
        label: dataset.display_name
      }));
    },
    getUsageDetails: async () => {
      try {
        if (!env.FEATUREFLAG_TRAINING_ENABLED) {
          return;
        }
        const response = await executeAPIRequest<TrainingUsageDetails>({
          method: "get",
          url: `${url}/taas/usage`,
          options: {
            replaceUrl: true
          }
        });
        set({ usageDetails: response });
      } catch (error) {
        console.error("Error fetching usage details:", error);
      }
    },
    clearError: () => {
      set({ trainingCreationError: null });
    },
    fetchTrainingProgress: async (jobId: string) => {
      if (get().isTrainingProgressLoading) {
        return null;
      }
      set({ isTrainingProgressLoading: true });
      try {
        const response = await executeAPIRequest<{
          training_progress: {
            logs: TrainingProgress[];
          };
        }>({
          method: "get",
          url: `${url}/taas/jobs/${jobId}/training-progress`,
          options: {
            axiosRetries: 0,
            replaceUrl: true
          }
        });
        set({ trainingProgressData: response.training_progress.logs });
        return response;
      } catch (error) {
        console.error("Error fetching training progress:", error);
        throw error;
      } finally {
        set({ isTrainingProgressLoading: false });
      }
    }
  };
});
